import { useCallback, useEffect, useRef, useState } from "react";
import { CheckboxListMolecule, IconLabelMolecule } from "..";
import { Identifier } from "../../../models";
import { IListValidator } from "../../../models/validator";
import { DetectClickArea } from "../../../utils/Clickable";
import { filterBySearch } from "../../../utils/FilterList";
import { categoryListToIdentifier } from "../../../utils/Metadata";
import { StringSanitizer } from "../../../utils/StringSanitizer";
import { ChipAtom, InputAtom } from "../../atoms";
import { getSelectedItemsFromCategoryList, ICategoryList } from "../../interfaces/CategoryList";
import { ICheckbox } from "../../interfaces/Checkbox";
import { LabelMolecule } from "../label";
import styles from "./styles.module.scss";

const stringSanitizer = new StringSanitizer();

interface Props {
  tekst: {
    id: string;
    label: string;
    placeholder?: string;
    tooltip?: string[];
  };
  categories: ICategoryList[];
  selected: Identifier[];
  required?: boolean;
  disabled?: boolean;
  singleSelect?: boolean;
  search?: boolean;
  validations?: IListValidator[];
  validateOpties?: boolean;
  inputRef?: (ref: HTMLInputElement) => void;
  onUpdated: (id: Identifier) => void;
}

export function SelectDropdownMolecule(props: Readonly<Props>) {
  const [showValidationMessage, setShowValidationMessage] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const containerRef = useRef<HTMLElement>();

  const selectedItemsList = getSelectedItemsFromCategoryList(props.categories);
  const validationErrors = getValidationErrors(props);
  const showValidationError = validationErrors.length > 0 && showValidationMessage && !props.disabled;

  const { classNameControls, classNameInput, classNameResults } = getClassNames({
    hasSelection: selectedItemsList.length > 0 || props.selected?.length > 0,
    disabled: props.disabled,
    expanded,
    showValidationError,
  });

  const collapse = () => {
    if (expanded) {
      setExpanded(false);
      setShowValidationMessage(true);
    }
  };

  const onClick = (cb: ICheckbox | Identifier) => {
    props.onUpdated({ id: stringSanitizer.extractIdentifier(`${props.tekst.id}_${cb.id}`), label: cb.label });
    props.singleSelect && collapse();
    setSearchTerm("");
  };

  const detectClickArea = useCallback(
    (event: MouseEvent) => {
      const clicked = DetectClickArea(containerRef, event.target);
      if (clicked && !expanded) {
        setExpanded(true);
      } else if (!clicked) {
        setSearchTerm("");
        collapse();
      }
    },
    [containerRef, setSearchTerm, expanded, setExpanded]
  );

  useEffect(() => {
    setSearchTerm("");
    document.addEventListener("mousedown", detectClickArea);
    return () => {
      document.removeEventListener("mousedown", detectClickArea);
    };
  }, [expanded, detectClickArea]);

  const selectedList = (): Identifier[] => {
    const castList: Identifier[] = selectedItemsList.map((item: ICheckbox) => {
      return { id: item.id, label: item.label };
    });
    const fullList: Identifier[] = props.selected?.concat(castList);
    const nonDublicatedList: Identifier[] = fullList?.filter((value: Identifier, index: number) => {
      const _value = JSON.stringify(value);
      return (
        index ===
        fullList.findIndex((obj) => {
          return JSON.stringify(obj) === _value;
        })
      );
    });

    return nonDublicatedList;
  };

  let chipElements = selectedList()?.map((item: Identifier) =>
    props.singleSelect ? (
      <div key={`div_${item.id}`} className={styles.singleSelect}>
        {item.label}
      </div>
    ) : (
      <ChipAtom
        disabled={props.disabled}
        key={item.id}
        text={item.label!}
        onRemove={() => {
          onClick(item);
        }}
      />
    )
  );

  const assignContainerRef = (currentlySelectedRef: HTMLInputElement) => {
    containerRef.current = currentlySelectedRef;
  };

  const showTextInput = props.search === undefined || props.search;
  const showContent = !props.disabled;
  const showPlaceHolder = props.search === false && selectedList().length <= 0;
  const placeholder = selectedList()?.length > 0 ? undefined : props.tekst.placeholder;
  const filteredOptions = filterBySearch(props.categories, searchTerm);
  const checkboxList = filteredOptions?.length > 0 && <CheckboxListMolecule list={filteredOptions} onClick={onClick} />;

  return (
    <div className={styles.container}>
      <LabelMolecule
        id={props.tekst.id + "_label"}
        for={props.tekst.id + "_input"}
        label={props.tekst.label}
        tooltip={props.tekst.tooltip}
        required={props.required}
      />
      <div className={classNameInput} ref={assignContainerRef}>
        <div id={props.tekst.id} key={"input_" + props.tekst.label} className={classNameControls}>
          {chipElements}
          {showTextInput && (
            <InputAtom
              id={props.tekst.id + "_input"}
              inputRef={props.inputRef}
              onEscape={() => {
                collapse();
              }}
              onBlur={() => setShowValidationMessage(true)}
              type={"text"}
              value={searchTerm}
              placeholder={placeholder}
              onChange={setSearchTerm}
              disabled={props.disabled}
            />
          )}
          {showPlaceHolder && <p className={styles.nosearch}>{props.tekst.placeholder}</p>}
        </div>
        {showContent && (
          <div key={"options_" + props.tekst.label} className={classNameResults}>
            {checkboxList === false ? "Geen resultaten" : checkboxList}
          </div>
        )}
      </div>
      {showValidationError && <IconLabelMolecule icon={"icon-alert-red"} label={validationErrors[0]} type={"alert"} />}
    </div>
  );
}

function getValidationErrors(props: Props): string[] {
  return (
    props.validations
      ?.map((validate: IListValidator) => {
        let error = validate.validate(props.selected);

        if (!error && props.validateOpties) {
          error = validate.validate(
            props.selected,
            categoryListToIdentifier(props.categories),
            props.tekst.label.toLowerCase()
          );
        }

        return error ?? "";
      })
      .filter((err) => err.length > 0) ?? []
  );
}

function getClassNames({
  hasSelection,
  disabled,
  expanded,
  showValidationError,
}: {
  hasSelection: boolean;
  disabled?: boolean;
  expanded: boolean;
  showValidationError: boolean;
}) {
  return {
    classNameControls: [styles.controls, hasSelection && styles["controls--selected"]].filter(Boolean).join(" "),

    classNameInput: [
      styles.select,
      disabled && styles["container--disabled"],
      !disabled && expanded && styles["select--expand"],
      showValidationError && styles["select--alert"],
    ]
      .filter(Boolean)
      .join(" "),

    classNameResults: [
      styles.results,
      expanded && styles["results--expand"],
      showValidationError && styles["results--alert"],
    ]
      .filter(Boolean)
      .join(" "),
  };
}
